import { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { DatePicker, InputNumber, Select } from "antd";
import { LockOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import {
    instantToCompanyMinutes,
    isMinuteWithinWindow,
    minutesToLabel,
    timeZoneAbbreviation,
} from "../../../../../helpers/operationalHours";
import {
    addExtensionDays,
    buildCalendarRangesFromPayload,
    calendarDayOf,
    getExtensionTimeOptionsForDate,
    isDayBlockedByCalendar,
    isExtensionDayFullyBlocked,
    isExtensionRangeBlocked,
    isExtensionTimeBlocked,
    rangeCrossesBlockedDay,
} from "../../../lib/reservationExtension/extensionAvailability";
import { fetchCarCalendarBlocks } from "../../../lib/reservationExtension/extensionApi";

const DATE_FORMAT = "MM/DD/YYYY";
const TIME_FORMAT = "HH:mm";
const PREVIEW_FORMAT = "MMM D, YYYY h:mm A";
const MAX_EXTENSION_DAYS = 365;

const daysBetween = (from, toIso) => {
    if (!from || !toIso) return null;
    const days = Math.round(dayjs(toIso).diff(from, "hour") / 24);
    return days >= 1 ? days : null;
};

/**
 * Step 1 of the extend modal: choose the new drop-off. Ported from the booking frontend's
 * `ExtendDateTimePicker` so staff get the same rules as customers.
 *
 * - `extendByDay` (company preference): a number of days, each adding 24 hours to the current
 *   drop-off.
 * - Otherwise: a calendar day + half-hour slot, picked on the branch's clock.
 *
 * Either way the car must be free for the whole extension and the new drop-off must fall inside
 * operational hours — the same checks the server makes. `onChange` receives a UTC ISO string,
 * `onConflictChange` whether the current choice is blocked.
 */
const ExtendDropoffPicker = ({
    resDetails,
    value,
    onChange,
    onConflictChange,
    extendByDay,
    branchTimeZone,
    bookingHours,
    bufferMinutes,
    t,
}) => {
    const extensionStart = useMemo(() => {
        const parsed = dayjs(resDetails?.exactDropoffTime || resDetails?.dropoffDate);
        return parsed.isValid() ? parsed : null;
    }, [resDetails?.exactDropoffTime, resDetails?.dropoffDate]);

    const [calendarPayload, setCalendarPayload] = useState(null);
    const carId = resDetails?.carSelected?.id ? String(resDetails.carSelected.id) : "";

    useEffect(() => {
        if (!carId) return undefined;
        let cancelled = false;
        fetchCarCalendarBlocks(carId)
            .then((payload) => {
                if (!cancelled) setCalendarPayload(payload);
            })
            .catch(() => {
                // Without the calendar only the "after current drop-off" and hours checks apply;
                // the server still rejects a conflicting extension.
                if (!cancelled) setCalendarPayload(null);
            });
        return () => {
            cancelled = true;
        };
    }, [carId]);

    const { reservationRanges, carBlockRanges, blockedTimeRanges } = useMemo(
        () => buildCalendarRangesFromPayload(calendarPayload, { bufferMinutes, excludeReservation: resDetails }),
        [bufferMinutes, calendarPayload, resDetails],
    );

    // `value` is a UTC instant; read it on the branch's clock for the date and time fields.
    const dropoffAtBranch = useMemo(() => {
        if (!value) return null;
        const parsed = dayjs(value);
        return parsed.isValid() ? parsed.tz(branchTimeZone) : null;
    }, [branchTimeZone, value]);
    // The date field works with plain calendar days; hand it the branch's day.
    const dropoffDay = useMemo(
        () => (dropoffAtBranch ? dayjs(dropoffAtBranch.format("YYYY-MM-DD"), "YYYY-MM-DD") : null),
        [dropoffAtBranch],
    );
    const dropoffTime = dropoffAtBranch ? dropoffAtBranch.format(TIME_FORMAT) : undefined;
    const [timeOpen, setTimeOpen] = useState(false);

    const timeOptionsForDay = useMemo(() => {
        const options = getExtensionTimeOptionsForDate(
            dropoffDay,
            blockedTimeRanges,
            extensionStart ?? dayjs(),
            branchTimeZone,
        );
        if (!bookingHours) return options;
        // Outside-hours slots stay visible but locked, so staff can see the branch's hours.
        return options.map((option) => {
            const [hour, minute] = option.value.split(":").map(Number);
            return isMinuteWithinWindow(hour * 60 + minute, bookingHours.hours)
                ? option
                : { ...option, disabled: true, locked: true };
        });
    }, [blockedTimeRanges, bookingHours, branchTimeZone, dropoffDay, extensionStart]);

    const isDateDisabled = useCallback(
        (current) => {
            if (!current || !extensionStart) return false;
            if (current.startOf("day").isBefore(calendarDayOf(extensionStart, branchTimeZone))) return true;
            if (rangeCrossesBlockedDay(extensionStart, current, reservationRanges, carBlockRanges, branchTimeZone)) {
                return true;
            }
            if (isDayBlockedByCalendar(current, reservationRanges, carBlockRanges, branchTimeZone)) return true;
            return isExtensionDayFullyBlocked(current, blockedTimeRanges, extensionStart, branchTimeZone);
        },
        [blockedTimeRanges, branchTimeZone, carBlockRanges, extensionStart, reservationRanges],
    );

    const outsideHours = useMemo(() => {
        if (!bookingHours || !value) return false;
        const minutes = instantToCompanyMinutes(value, bookingHours.timeZone);
        return minutes !== null && !isMinuteWithinWindow(minutes, bookingHours.hours);
    }, [bookingHours, value]);

    let error = "";
    if (extendByDay) {
        if (
            extensionStart &&
            dropoffAtBranch &&
            isExtensionRangeBlocked(extensionStart, dropoffAtBranch, blockedTimeRanges)
        ) {
            error = t("extendReservation.carUnavailable");
        } else if (outsideHours) {
            error = t("extendReservation.outsideOperationalHours");
        }
    } else if (dropoffDay) {
        if (isDateDisabled(dropoffDay)) {
            error = t("extendReservation.dateUnavailable");
        } else if (
            dropoffTime &&
            extensionStart &&
            isExtensionTimeBlocked(dropoffDay, dropoffTime, blockedTimeRanges, extensionStart, branchTimeZone)
        ) {
            error = dropoffAtBranch?.isAfter(extensionStart)
                ? t("extendReservation.carUnavailable")
                : t("extendReservation.mustBeAfterCurrentDropoff");
        } else if (outsideHours) {
            error = t("extendReservation.outsideOperationalHours");
        }
    }

    const hasConflict = Boolean(error);
    useEffect(() => {
        onConflictChange(hasConflict);
    }, [hasConflict, onConflictChange]);

    // Seeded from `value` so stepping back from the invoice keeps the number that was entered.
    const [days, setDays] = useState(() => daysBetween(extensionStart, value));

    const handleDaysChange = (next) => {
        const wholeDays = typeof next === "number" && Number.isFinite(next) ? Math.floor(next) : null;
        setDays(wholeDays);
        onChange(
            wholeDays && wholeDays >= 1 && extensionStart
                ? addExtensionDays(extensionStart, wholeDays).toISOString()
                : "",
        );
    };

    const commitDropoff = (day, time) => {
        if (!day?.isValid()) return;
        // Anchor the picked day + time to the branch's clock, not the admin's device.
        const combined = dayjs.tz(`${day.format("YYYY-MM-DD")} ${time || "00:00"}`, "YYYY-MM-DD HH:mm", branchTimeZone);
        if (combined.isValid()) onChange(combined.toISOString());
    };

    const handleDateChange = (day) => {
        const keepTime =
            dropoffTime &&
            extensionStart &&
            !isExtensionTimeBlocked(day, dropoffTime, blockedTimeRanges, extensionStart, branchTimeZone)
                ? dropoffTime
                : undefined;
        commitDropoff(day, keepTime);
        if (day?.isValid()) window.setTimeout(() => setTimeOpen(true), 0);
    };

    const handleTimeChange = (time) => {
        commitDropoff(dropoffDay ?? (extensionStart ? calendarDayOf(extensionStart, branchTimeZone) : dayjs()), time);
        setTimeOpen(false);
    };

    const zone = timeZoneAbbreviation(branchTimeZone);
    const hoursHint = bookingHours
        ? t("extendReservation.openHours", {
              hours: `${minutesToLabel(bookingHours.hours.startMinutes)} – ${minutesToLabel(bookingHours.hours.endMinutes)} ${zone}`.trim(),
          })
        : t("extendReservation.timesIn", { zone: zone || branchTimeZone });

    const currentDropoffLabel = extensionStart
        ? `${extensionStart.tz(branchTimeZone).format(PREVIEW_FORMAT)} ${zone}`.trim()
        : "—";

    return (
        <div className="space-y-4">
            <div className="rounded-xl bg-[#F7F7FB] px-4 py-3 text-sm">
                <span className="fontRegular text-[#7f8c8d]">{t("extendReservation.currentDropoff")}: </span>
                <span className="fontSemi text-[#2F2F46]">{currentDropoffLabel}</span>
            </div>

            <div>
                <p className="fontSemi mb-2 text-sm text-[#2F2F46]">
                    {extendByDay ? t("extendReservation.daysLabel") : t("extendReservation.dateTimeLabel")}
                </p>

                {extendByDay ? (
                    <InputNumber
                        value={days}
                        onChange={handleDaysChange}
                        min={1}
                        max={MAX_EXTENSION_DAYS}
                        precision={0}
                        step={1}
                        placeholder="1"
                        status={error ? "error" : undefined}
                        addonAfter={t("extendReservation.daysUnit")}
                        className="!w-full"
                    />
                ) : (
                    <div className="grid grid-cols-[1.4fr_1fr] gap-2">
                        <DatePicker
                            value={dropoffDay}
                            onChange={handleDateChange}
                            format={DATE_FORMAT}
                            allowClear={false}
                            disabledDate={isDateDisabled}
                            status={error ? "error" : undefined}
                            className="!w-full"
                        />
                        <Select
                            value={dropoffTime}
                            options={timeOptionsForDay}
                            optionRender={(option) => (
                                <span className="flex items-center justify-between gap-2">
                                    <span>{option.label}</span>
                                    {option.data?.locked ? <LockOutlined className="text-[10px]" /> : null}
                                </span>
                            )}
                            placeholder={t("extendReservation.timePlaceholder")}
                            open={timeOpen}
                            onOpenChange={setTimeOpen}
                            onChange={handleTimeChange}
                            status={error ? "error" : undefined}
                            listHeight={280}
                            className="w-full"
                        />
                    </div>
                )}

                {error ? <p className="mt-1 text-xs text-red-500">{error}</p> : null}
                {extendByDay && dropoffAtBranch ? (
                    <p className="fontSemi mt-2 text-sm text-[#2F2F46]">
                        {t("extendReservation.newDropoffPreview", {
                            date: `${dropoffAtBranch.format(PREVIEW_FORMAT)} ${zone}`.trim(),
                        })}
                    </p>
                ) : null}
                <p className="mt-1 text-xs text-[#7f8c8d]">{hoursHint}</p>
            </div>
        </div>
    );
};

ExtendDropoffPicker.propTypes = {
    resDetails: PropTypes.object.isRequired,
    value: PropTypes.string,
    onChange: PropTypes.func.isRequired,
    onConflictChange: PropTypes.func.isRequired,
    extendByDay: PropTypes.bool,
    branchTimeZone: PropTypes.string.isRequired,
    bookingHours: PropTypes.shape({
        hours: PropTypes.shape({ startMinutes: PropTypes.number, endMinutes: PropTypes.number }),
        timeZone: PropTypes.string,
    }),
    bufferMinutes: PropTypes.number,
    t: PropTypes.func.isRequired,
};

export default ExtendDropoffPicker;
